import type { Scene } from "@babylonjs/core/scene";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateIcoSphere } from "@babylonjs/core/Meshes/Builders/icoSphereBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { hex } from "../palette.ts";

/**
 * A projectile in flight: a bright ball in the shooter's colour with a
 * spinning ring, so it reads as a threat (and its owner) at low resolution.
 */
export class ProjectileView {
  private readonly ball: Mesh;
  private readonly ring: Mesh;
  private time = 0;

  /** `css`: the shooter's colour (its team's in a team match). */
  constructor(scene: Scene, radius: number, css: string) {
    const color = hex(css);
    const material = new StandardMaterial("projectile", scene);
    material.disableLighting = true;
    material.emissiveColor = Color3.Lerp(color, Color3.White(), 0.45);
    material.specularColor = Color3.Black();

    this.ball = CreateIcoSphere("projectile", { radius, subdivisions: 1, flat: true }, scene);
    this.ball.material = material;
    this.ball.isPickable = false;

    this.ring = CreateTorus("projectile-ring", { diameter: radius * 3, thickness: radius * 0.35, tessellation: 10 }, scene);
    this.ring.material = material;
    this.ring.isPickable = false;
    this.ring.parent = this.ball;
  }

  update(x: number, y: number, z: number, fx: number, fz: number, dt: number): void {
    this.time += dt;
    this.ball.position.set(x, y, z);
    this.ball.rotation.y = Math.atan2(fx, fz);
    this.ring.rotation.x = Math.PI / 2;
    this.ring.rotation.y = this.time * 14;
  }

  dispose(): void {
    this.ball.dispose(false, true);
  }
}
